// Slice-Plugin registry for board object types. Each <o2d-whiteboard> instance
// owns one ObjectRegistry; slices register their plugin in `firstUpdated`.
// See PRD-E TR8 and TD-001 §7 for the rationale.

import type Konva from 'konva';
import type { BoardObject } from './interfaces/board-object.interface.js';
import type { Point, Size } from './bus/ui-action.events.js';
import type { ObjectId } from './bus/branded-types.js';
import type { WhiteboardBus } from './bus/whiteboard-bus.js';
import type { BoardState } from './state/board-state.js';
import type { CapabilityDescriptor } from './capabilities/capability.interface.js';

export type { CapabilityDescriptor };

export interface SliceRenderContext {
  /** Live viewport from BoardState; populated by the caller, never hardcoded. */
  readonly viewport: { readonly x: number; readonly y: number; readonly scale: number };
  /**
   * Optional read-back of the current object snapshot from BoardState.
   * Used by renderers that perform deferred dimension corrections (e.g. the
   * image slice's Variant-C natural-size fix-up after async image load).
   * Renderers MUST treat `undefined` (object deleted, e.g. via Undo) as a no-op
   * to remain undo-safe — see PRD-F4 FC20.
   */
  readonly getObject?: (id: ObjectId) => BoardObject | undefined;
  /**
   * Optional state mutation outside the History pipeline. Use ONLY for cosmetic
   * corrections that must not appear as user-visible Undo entries (e.g. image
   * dimension fix-up after natural-size discovery — PRD-F4 D6 / TD-001 §11 P5.4
   * Variant C). Production callers wire this to a non-history-emitting state
   * patch; tests may stub it out.
   */
  readonly updateObject?: (id: ObjectId, patch: Partial<BoardObject>) => void;
  /**
   * Optional bus reference for renderers that need to dispatch UiActions in
   * response to in-canvas interactions (PRD-F6 §TR4 / Decision E6). The
   * textbox slice uses this for the checklist-item click → toggle path,
   * where the rasterized checkbox glyphs are non-interactive and the
   * renderer's `click` listener emits `textbox/toggle-checklist-item`
   * instead. Optional so that pre-existing slices remain unaffected.
   */
  readonly bus?: WhiteboardBus;
}

export interface EditorContext {
  readonly bus: WhiteboardBus;
  readonly state: BoardState;
}

/**
 * Marker type for pluggable strategy registries (PRD-F5 §TR8b).
 *
 * Concrete implementations live in the slices that need them (e.g.
 * `LinePathStrategyRegistry` in `features/line/strategies/`). Slices that
 * implement `registerStrategies` cast this marker to their expected concrete
 * registry internally — keeping `core/` free of slice-level imports preserves
 * the layering rule (TD-001 §7).
 *
 * The marker has no methods because there is no shared API across registries.
 * Adding members later would force every consumer through a single shape.
 */
export type StrategyRegistry = object;

export interface SlicePlugin<T extends BoardObject> {
  readonly type: T['type'];
  readonly role: 'primary' | 'connector' | 'decoration';
  readonly capabilities: readonly CapabilityDescriptor[];
  readonly display: { iconUrl: string; label: string };
  createDefaults(scale: number, point: Point, sizeOpt: Size | null): T;
  /** Plugin returns the Konva node; the caller (element) attaches it to contentLayer. */
  createNode(obj: T, ctx: SliceRenderContext): Konva.Node;
  /** Consumers may use `instanceof Konva.Group` checks (see board-object-renderer pattern). */
  updateNode(node: Konva.Node, obj: T, ctx: SliceRenderContext): void;
  isAnchorTarget?(): boolean;
  /**
   * Optional: the world-space box a line should actually dock to, when it
   * differs from the object's own x/y/width/height. The person uses this to
   * anchor lines to its visible avatar+name instead of its larger, padded
   * object box (so connector handles and docked endpoints hug the person).
   * Omitted → callers fall back to the object's bounding box.
   */
  anchorBounds?(obj: T): { x: number; y: number; width: number; height: number };
  openEditor?(id: ObjectId, ctx: EditorContext): void;
  /**
   * Optional: migrate a persisted/legacy payload into the current in-memory
   * shape before it enters `BoardState`. Called by the element's `config`
   * setter for every loaded object. Slices return the same reference (or a
   * patched copy) — callers MUST treat the return value as authoritative.
   * Used by the textbox slice to up-convert legacy `text` HTML into a TipTap
   * `document` (PRD-RTE-TIPTAP-document-model §TR3 / §T2.5).
   */
  hydrate?(obj: T): T;
  /**
   * Optional: register pluggable strategies (e.g. `LinePathStrategy` for the
   * line slice) into the per-element strategy registry. Called from
   * `firstUpdated` after `register(plugin)`. Slices that don't need
   * strategies omit this method — callers MUST use optional-chaining.
   */
  registerStrategies?(registry: StrategyRegistry): void;
}

/**
 * Map-backed plugin registry. One instance per `<o2d-whiteboard>` element so
 * multiple boards on a single page stay isolated (TD-001 §2).
 *
 * Variance bridge: `register<T>` casts to `SlicePlugin<BoardObject>` because
 * `SlicePlugin<T>` is invariant in `T`. The cast is sound as long as callers
 * of `get(type).createNode/updateNode` only pass objects whose `type` matches
 * the registered plugin's `type` — see TR8 invariant.
 */
export class ObjectRegistry {
  readonly #plugins = new Map<BoardObject['type'], SlicePlugin<BoardObject>>();

  register<T extends BoardObject>(plugin: SlicePlugin<T>): void {
    this.#plugins.set(plugin.type, plugin as unknown as SlicePlugin<BoardObject>);
  }

  get(type: BoardObject['type']): SlicePlugin<BoardObject> | undefined {
    return this.#plugins.get(type);
  }

  has(type: BoardObject['type']): boolean {
    return this.#plugins.has(type);
  }

  getPrimaries(): readonly SlicePlugin<BoardObject>[] {
    const result: SlicePlugin<BoardObject>[] = [];
    for (const plugin of this.#plugins.values()) {
      if (plugin.role === 'primary') result.push(plugin);
    }
    return result;
  }

  // Returns capabilities of the given type that carry a view aspect.
  // Empty array if the type is not registered (TR2).
  getViewCapabilities(type: BoardObject['type']): readonly CapabilityDescriptor[] {
    const plugin = this.#plugins.get(type);
    if (!plugin) return [];
    return plugin.capabilities.filter((cap) => 'view' in cap);
  }

  // Returns capabilities of the given type that carry an edit aspect.
  // Empty array if the type is not registered (TR2).
  getEditCapabilities(type: BoardObject['type']): readonly CapabilityDescriptor[] {
    const plugin = this.#plugins.get(type);
    if (!plugin) return [];
    return plugin.capabilities.filter((cap) => 'edit' in cap);
  }
}
